import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Eye,
  Clock,
  Link as LinkIcon,
  Copy,
  Check,
  ExternalLink,
  Trash2,
  Users,
  TrendingUp,
  Globe,
  BarChart3,
  Download,
  Loader2,
} from 'lucide-react'
import { useVideoStore } from '../stores/videoStore'
import VideoPlayer from '../components/VideoPlayer'
import LinkCreator from '../components/LinkCreator'
import { Tabs, Spinner, EmptyState, Badge, StatCard } from '../components/common'
import { ViewsLineChart, EngagementHeatmap, StatsPieChart, ChartLegend } from '../components/AnalyticsChart'
import { analyticsApi, videosApi } from '../services/api'
import { formatDuration, formatNumber, useClipboard } from '../hooks'
import { format } from 'date-fns'
import type { VideoAnalyticsData } from '../types'

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentVideo, links, isLoading, error, fetchVideo, fetchLinks, deleteLink, updateVideo, deleteVideo } =
    useVideoStore()
  const [activeTab, setActiveTab] = useState('links')
  const [showLinkCreator, setShowLinkCreator] = useState(false)
  const { copied, copy } = useClipboard()
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [videoAnalytics, setVideoAnalytics] = useState<VideoAnalyticsData | null>(null)
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const handleDownload = async () => {
    if (!currentVideo) return
    if (currentVideo.download_url) {
      window.open(currentVideo.download_url, '_blank')
      return
    }
    setDownloading(true)
    try {
      const { download_url, status } = await videosApi.getDownloadUrl(currentVideo.id)
      if (status === 'ready') {
        window.open(download_url, '_blank')
      } else {
        alert('Download is being prepared by Cloudflare. Please try again in a minute.')
      }
    } catch {
      alert('Failed to get download URL.')
    } finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchVideo(id)
      fetchLinks(id)
    }
  }, [id, fetchVideo, fetchLinks])

  useEffect(() => {
    if (currentVideo) {
      setEditTitle(currentVideo.title)
      setEditDescription(currentVideo.description || '')
    }
  }, [currentVideo])

  useEffect(() => {
    if (activeTab === 'analytics' && id && !videoAnalytics && !isLoadingAnalytics) {
      setIsLoadingAnalytics(true)
      analyticsApi.getVideoAnalytics(id)
        .then((data) => setVideoAnalytics(data as VideoAnalyticsData))
        .catch(() => {})
        .finally(() => setIsLoadingAnalytics(false))
    }
  }, [activeTab, id, videoAnalytics, isLoadingAnalytics])

  const handleSaveChanges = async () => {
    if (!id || !currentVideo) return
    setIsSaving(true)
    try {
      await updateVideo(id, { title: editTitle, description: editDescription })
    } catch (err) {
      // Error is handled by store
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteVideo = async () => {
    if (!id) return
    if (!confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return
    }
    try {
      await deleteVideo(id)
      navigate('/videos')
    } catch (err) {
      // Error is handled by store
    }
  }

  if (isLoading && !currentVideo) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !currentVideo) {
    return (
      <EmptyState
        title="Video not found"
        description={error || 'The video you are looking for does not exist'}
        action={
          <Link to="/videos" className="btn-primary">
            Back to Videos
          </Link>
        }
      />
    )
  }

  const tabs = [
    { id: 'links', label: `Links (${links.length})` },
    { id: 'analytics', label: 'Analytics' },
    { id: 'settings', label: 'Settings' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/videos"
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{currentVideo.title}</h1>
          <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {formatNumber(currentVideo.total_views)} views
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDuration(currentVideo.duration || 0)}
            </span>
            <Badge
              variant={currentVideo.status === 'ready' ? 'success' : 'warning'}
            >
              {currentVideo.status}
            </Badge>
          </div>
        </div>

        {currentVideo.status === 'ready' && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-secondary"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download
          </button>
        )}
        <button
          onClick={() => setShowLinkCreator(true)}
          className="btn-primary"
          disabled={currentVideo.status !== 'ready'}
        >
          <Plus className="w-4 h-4" />
          Create Link
        </button>
      </div>

      {/* Video player */}
      {currentVideo.embed_url && (
        <div className="w-full max-w-5xl">
          <VideoPlayer
            embedUrl={currentVideo.embed_url}
            title={currentVideo.title}
            className="shadow-xl"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'links' && (
          <div className="space-y-4">
            {links.length === 0 ? (
              <EmptyState
                icon={<LinkIcon className="w-12 h-12" />}
                title="No tracking links yet"
                description="Create a tracking link to share this video and monitor engagement"
                action={
                  <button
                    onClick={() => setShowLinkCreator(true)}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4" />
                    Create Link
                  </button>
                }
              />
            ) : (
              <div className="space-y-3">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className="card flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/links/${link.id}`}
                          className="font-medium text-white hover:text-primary-400"
                        >
                          {link.recipient_name || link.short_code}
                        </Link>
                        {link.recipient_email && (
                          <span className="text-sm text-slate-400">
                            {link.recipient_email}
                          </span>
                        )}
                        {!link.is_active && (
                          <Badge variant="danger">Inactive</Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                        <span>{link.view_count} views</span>
                        <span>{Math.round(link.avg_completion)}% avg completion</span>
                        {link.last_viewed_at && (
                          <span>
                            Last viewed {format(new Date(link.last_viewed_at), 'MMM d')}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copy(link.short_url)}
                        className="btn-ghost"
                        title="Copy link"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <a
                        href={link.short_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost"
                        title="Open link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => {
                          if (confirm('Delete this link?')) {
                            deleteLink(link.id)
                          }
                        }}
                        className="btn-ghost text-red-400 hover:text-red-300"
                        title="Delete link"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {isLoadingAnalytics ? (
              <div className="flex items-center justify-center h-64">
                <Spinner size="lg" />
              </div>
            ) : videoAnalytics ? (
              <>
                {/* Summary stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    label="Total Views"
                    value={formatNumber(videoAnalytics.summary.total_views)}
                    icon={<Eye className="w-5 h-5" />}
                  />
                  <StatCard
                    label="Unique Viewers"
                    value={formatNumber(videoAnalytics.summary.unique_viewers)}
                    icon={<Users className="w-5 h-5" />}
                  />
                  <StatCard
                    label="Total Watch Time"
                    value={formatDuration(videoAnalytics.summary.total_watch_time)}
                    icon={<Clock className="w-5 h-5" />}
                  />
                  <StatCard
                    label="Avg. Completion"
                    value={`${Math.round(videoAnalytics.summary.avg_completion)}%`}
                    icon={<TrendingUp className="w-5 h-5" />}
                  />
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Views over time */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4">Views Over Time</h3>
                    {videoAnalytics.views_by_day.length > 0 ? (
                      <ViewsLineChart
                        data={videoAnalytics.views_by_day}
                        dataKey="views"
                        xDataKey="date"
                        height={200}
                      />
                    ) : (
                      <div className="h-48 flex items-center justify-center text-slate-400">
                        No view data available
                      </div>
                    )}
                  </div>

                  {/* Engagement heatmap */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4">Viewer Retention</h3>
                    {videoAnalytics.engagement_heatmap.length > 0 ? (
                      <EngagementHeatmap
                        data={videoAnalytics.engagement_heatmap}
                        dropOffPoints={videoAnalytics.drop_off_points}
                        videoDuration={currentVideo?.duration}
                        height={200}
                      />
                    ) : (
                      <div className="h-48 flex items-center justify-center text-slate-400">
                        No engagement data available
                      </div>
                    )}
                  </div>
                </div>

                {/* Links performance table */}
                <div className="card">
                  <h3 className="text-lg font-semibold text-white mb-4">Links Performance</h3>
                  {videoAnalytics.links_performance.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-700">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Recipient</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Views</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Avg. Completion</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {videoAnalytics.links_performance.map((link) => (
                            <tr key={link.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                              <td className="py-3 px-4">
                                <Link to={`/links/${link.id}`} className="text-white hover:text-primary-400">
                                  {link.recipient_name || link.short_code}
                                </Link>
                                {link.recipient_email && (
                                  <div className="text-sm text-slate-400">{link.recipient_email}</div>
                                )}
                              </td>
                              <td className="text-right py-3 px-4 text-white">{link.views}</td>
                              <td className="text-right py-3 px-4 text-white">{link.avg_completion}%</td>
                              <td className="text-right py-3 px-4">
                                <Link
                                  to={`/links/${link.id}`}
                                  className="text-primary-400 hover:text-primary-300 text-sm"
                                >
                                  View Details
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      No tracking links created yet
                    </div>
                  )}
                </div>

                {/* Geographic and device breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Geographic breakdown */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      Viewers by Location
                    </h3>
                    {videoAnalytics.geo_breakdown && videoAnalytics.geo_breakdown.length > 0 ? (
                      <>
                        <StatsPieChart
                          data={videoAnalytics.geo_breakdown.map((g) => ({
                            name: g.country_name || g.country,
                            views: g.views,
                          }))}
                          dataKey="views"
                          nameKey="name"
                          height={200}
                        />
                        <ChartLegend
                          items={videoAnalytics.geo_breakdown.slice(0, 5).map((g) => ({
                            name: g.country_name || g.country,
                            value: g.views,
                          }))}
                        />
                      </>
                    ) : (
                      <div className="h-48 flex items-center justify-center text-slate-400">
                        No geographic data available
                      </div>
                    )}
                  </div>

                  {/* Device breakdown */}
                  <div className="card">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Viewers by Device
                    </h3>
                    {videoAnalytics.device_breakdown && videoAnalytics.device_breakdown.devices && videoAnalytics.device_breakdown.devices.length > 0 ? (
                      <>
                        <StatsPieChart
                          data={videoAnalytics.device_breakdown.devices.map((d: { type: string; count: number }) => ({
                            name: d.type.charAt(0).toUpperCase() + d.type.slice(1),
                            views: d.count,
                          }))}
                          dataKey="views"
                          nameKey="name"
                          height={200}
                        />
                        <ChartLegend
                          items={videoAnalytics.device_breakdown.devices.map((d: { type: string; count: number }) => ({
                            name: d.type.charAt(0).toUpperCase() + d.type.slice(1),
                            value: d.count,
                          }))}
                        />
                      </>
                    ) : (
                      <div className="h-48 flex items-center justify-center text-slate-400">
                        No device data available
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <EmptyState
                icon={<BarChart3 className="w-12 h-12" />}
                title="No analytics data"
                description="Analytics will appear once viewers start watching your video"
              />
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="card space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-400 mb-1">
                Description
              </label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="input resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="btn-secondary"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleDeleteVideo}
                disabled={isSaving}
                className="btn-danger"
              >
                Delete Video
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Link creator modal */}
      {showLinkCreator && id && (
        <LinkCreator
          videoId={id}
          onClose={() => setShowLinkCreator(false)}
          onSuccess={() => fetchLinks(id)}
        />
      )}
    </div>
  )
}
